/**
 * Timezone Store
 * Manages application timezone setting using Zustand
 * Used for displaying dates/times in the correct timezone
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Get browser's default timezone
const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

interface TimezoneState {
  timezone: string;
  actions: {
    setTimezone: (timezone: string) => void;
    resetToDefault: () => void;
  };
}

const useTimezoneStore = create<TimezoneState>()(
  persist(
    (set) => ({
      timezone: getBrowserTimezone(),
      actions: {
        setTimezone: (timezone: string) => set({ timezone }),
        resetToDefault: () => set({ timezone: getBrowserTimezone() }),
      },
    }),
    {
      name: 'app-timezone',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ timezone: state.timezone }),
    }
  )
);

// Selectors
export const useTimezone = () => useTimezoneStore((state) => state.timezone);
export const useTimezoneActions = () => useTimezoneStore((state) => state.actions);

// For use outside React components
export const getTimezone = () => useTimezoneStore.getState().timezone;

// Common timezone options (ordered by relevance for Middle East region)
export const TIMEZONE_OPTIONS = [
  // Middle East & Arabian Peninsula
  { value: 'Asia/Baghdad', label: 'بغداد (GMT+3)', offset: '+03:00' },
  { value: 'Asia/Riyadh', label: 'الرياض (GMT+3)', offset: '+03:00' },
  { value: 'Asia/Kuwait', label: 'الكويت (GMT+3)', offset: '+03:00' },
  { value: 'Asia/Qatar', label: 'قطر (GMT+3)', offset: '+03:00' },
  { value: 'Asia/Bahrain', label: 'البحرين (GMT+3)', offset: '+03:00' },
  { value: 'Asia/Dubai', label: 'دبي (GMT+4)', offset: '+04:00' },
  { value: 'Asia/Muscat', label: 'مسقط (GMT+4)', offset: '+04:00' },
  { value: 'Asia/Aden', label: 'عدن (GMT+3)', offset: '+03:00' },
  { value: 'Asia/Amman', label: 'عمّان (GMT+3)', offset: '+03:00' },
  { value: 'Asia/Beirut', label: 'بيروت (GMT+2)', offset: '+02:00' },
  { value: 'Asia/Damascus', label: 'دمشق (GMT+3)', offset: '+03:00' },
  { value: 'Asia/Jerusalem', label: 'القدس (GMT+2)', offset: '+02:00' },
  // North Africa
  { value: 'Africa/Cairo', label: 'القاهرة (GMT+2)', offset: '+02:00' },
  { value: 'Africa/Tripoli', label: 'طرابلس (GMT+2)', offset: '+02:00' },
  { value: 'Africa/Tunis', label: 'تونس (GMT+1)', offset: '+01:00' },
  { value: 'Africa/Algiers', label: 'الجزائر (GMT+1)', offset: '+01:00' },
  { value: 'Africa/Casablanca', label: 'الدار البيضاء (GMT+1)', offset: '+01:00' },
  // Common international
  { value: 'UTC', label: 'توقيت عالمي منسق (UTC)', offset: '+00:00' },
  { value: 'Europe/London', label: 'لندن (GMT+0)', offset: '+00:00' },
  { value: 'Europe/Paris', label: 'باريس (GMT+1)', offset: '+01:00' },
  { value: 'Europe/Istanbul', label: 'إسطنبول (GMT+3)', offset: '+03:00' },
  { value: 'America/New_York', label: 'نيويورك (GMT-5)', offset: '-05:00' },
];

export default useTimezoneStore;
